import { Configuration, LogLevel } from '@azure/msal-browser';
import { getRuntimeConfig } from 'src/utils/runtimeConfig';
import { EntraConfig } from './types';
import { authLogger } from './utils/logger';
import {
  createConfigurationError,
  handleAuthError,
  AuthErrorType,
} from './utils/errorHandling';

/**
 * Safely convert a configuration value to string, handling non-string types
 *
 * @param value - The configuration value to convert
 * @param defaultValue - Default value to use if conversion fails
 * @returns String representation of the value or default value
 */
function configValueToString(value: unknown, defaultValue: string): string {
  if (typeof value === 'string') {
    return value;
  }
  if (value === null || value === undefined) {
    return defaultValue;
  }
  // For non-string, non-null values, return default instead of stringifying
  return defaultValue;
}

/**
 * Get the appropriate redirect URI based on current environment
 * Supports localhost, LocalCan domains, and production environments
 *
 * @returns The redirect URI for the current environment
 */
function getRedirectUri(): string {
  const hostname = window.location.hostname;
  const protocol = window.location.protocol;
  const port = window.location.port;

  // LocalCan domain (.localcan.dev)
  if (hostname.includes('.localcan.dev')) {
    return `${protocol}//${hostname}/`;
  }

  // Localhost development
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    const portSuffix = port ? `:${port}` : '';
    return `${protocol}//${hostname}${portSuffix}/`;
  }

  // Production or other domains
  return `${protocol}//${hostname}/`;
}

/**
 * Get the appropriate post-logout redirect URI
 * Uses same logic as redirect URI for consistency
 */
function getPostLogoutRedirectUri(): string {
  return getRedirectUri();
}

/**
 * Parse comma-separated scope strings into an array of trimmed, non-empty scopes
 * Consolidates scope parsing logic used throughout the MSAL configuration
 *
 * @param scopesString - Comma-separated string of scopes
 * @returns Array of trimmed, non-empty scope strings
 */
function parseScopes(scopesString: string): string[] {
  return scopesString
    .split(',')
    .map((scope) => scope.trim())
    .filter((scope) => scope.length > 0);
}

/**
 * Get Entra ID configuration from runtime config with dynamic redirect URI resolution
 */
export function getEntraConfig(): EntraConfig {
  const config = getRuntimeConfig();

  const clientId = configValueToString(config.VITE_ENTRA_CLIENT_ID, '');
  const authority = configValueToString(config.VITE_ENTRA_AUTHORITY, '');

  // Use dynamic redirect URI resolution, with environment variable override
  const redirectUri = configValueToString(
    config.VITE_ENTRA_REDIRECT_URI,
    getRedirectUri(),
  );
  const postLogoutRedirectUri = configValueToString(
    config.VITE_ENTRA_POST_LOGOUT_REDIRECT_URI,
    getPostLogoutRedirectUri(),
  );
  const scopesString = configValueToString(
    config.VITE_ENTRA_SCOPES,
    'openid profile email User.Read',
  );

  // Validate required configuration
  if (!clientId) {
    throw createConfigurationError(
      'VITE_ENTRA_CLIENT_ID is required for Entra ID authentication',
      'getEntraConfig',
    );
  }

  if (!authority) {
    throw createConfigurationError(
      'VITE_ENTRA_AUTHORITY is required for Entra ID authentication',
      'getEntraConfig',
    );
  }

  return {
    clientId,
    authority,
    redirectUri,
    postLogoutRedirectUri,
    scopes: parseScopes(scopesString),
  };
}

/**
 * Enhanced MSAL configuration for redirect flow support
 * Supports both popup and redirect authentication flows based on feature flags
 */
export function createMsalConfig(): Configuration {
  try {
    const entraConfig = getEntraConfig();

    const msalConfig: Configuration = {
      auth: {
        clientId: entraConfig.clientId,
        authority: entraConfig.authority,
        redirectUri: entraConfig.redirectUri,
        postLogoutRedirectUri: entraConfig.postLogoutRedirectUri,
        navigateToLoginRequestUrl: true, // ✅ Enable for redirect flow
        knownAuthorities: [new URL(entraConfig.authority).hostname],
      },
      cache: {
        cacheLocation: 'sessionStorage', // Better for redirect flow
        storeAuthStateInCookie: true, // Required for redirect flow
        secureCookies: window.location.protocol === 'https:',
      },
      system: {
        loggerOptions: {
          loggerCallback: (level, message, containsPii) => {
            if (containsPii) return;
            console.info(`[MSAL ${LogLevel[level]}] ${message}`);
          },
          logLevel: import.meta.env.DEV ? LogLevel.Warning : LogLevel.Error,
          piiLoggingEnabled: false,
        },
        windowHashTimeout: 60000,
        iframeHashTimeout: 6000,
      },
    };

    return msalConfig;
  } catch (error) {
    handleAuthError(error, 'createMsalConfig', AuthErrorType.CONFIGURATION);
  }
}

/**
 * Multi-tenant identifiers that should not be treated as specific tenant IDs
 */
const MULTI_TENANT_IDENTIFIERS = new Set([
  'common',
  'organizations',
  'consumers',
]);

/**
 * Authority hostname patterns for tenant extraction
 */
const AUTHORITY_PATTERNS = {
  microsoftOnline: 'login.microsoftonline.com',
  azureB2C: '.b2clogin.com',
} as const;

/**
 * Extract tenant ID from Microsoft Entra ID authority URL
 * @param authority - The authority URL (e.g., https://login.microsoftonline.com/tenant-id)
 * @returns The tenant ID or null if not found/invalid
 */
export function extractTenantIdFromAuthority(authority: string): string | null {
  if (!authority || typeof authority !== 'string') {
    return null;
  }

  try {
    const url = new URL(authority);

    // Use pattern matching for different authority types
    if (url.hostname === AUTHORITY_PATTERNS.microsoftOnline) {
      return extractFromMicrosoftOnline(url);
    }

    if (url.hostname.includes(AUTHORITY_PATTERNS.azureB2C)) {
      return extractFromAzureB2C(url);
    }

    return null;
  } catch {
    // Invalid URL format
    return null;
  }
}

/**
 * Extract tenant ID from Microsoft Online authority
 */
function extractFromMicrosoftOnline(url: URL): string | null {
  const pathSegments = url.pathname.split('/').filter(Boolean);
  if (pathSegments.length === 0) {
    return null;
  }

  const tenantId = pathSegments[0];
  return MULTI_TENANT_IDENTIFIERS.has(tenantId) ? null : tenantId;
}

/**
 * Extract tenant ID from Azure AD B2C authority
 */
function extractFromAzureB2C(url: URL): string | null {
  const subdomain = url.hostname.split('.')[0];
  return subdomain && subdomain !== 'login' ? subdomain : null;
}

/**
 * Get scopes from environment variable (lazy evaluation)
 */
function getScopes(): string[] {
  const config = getRuntimeConfig();
  const scopesString = configValueToString(
    config.VITE_ENTRA_SCOPES,
    'openid,profile,email,User.Read',
  );
  const scopes = parseScopes(scopesString);

  // Log scopes in development mode for debugging
  authLogger.dev('🔍 MSAL Scopes being used:', scopes);
  authLogger.dev('🔍 Raw scopes string from config:', scopesString);

  return scopes;
}

/**
 * Extract custom API scope from environment configuration
 * Looks for scopes that match the pattern api://[guid]/[scope]
 */
export function getCustomApiScope(): string | null {
  const scopes = getScopes();
  const customApiScope = scopes.find(
    (scope) => scope.startsWith('api://') && scope.includes('/'),
  );
  return customApiScope || null;
}

/**
 * Extract Microsoft Graph scopes from environment configuration
 * Defaults to User.Read if not specified
 */
export function getMicrosoftGraphScopes(): string[] {
  const scopes = getScopes();
  const graphScopes = scopes.filter(
    (scope) =>
      scope === 'User.Read' ||
      scope === 'User.ReadBasic.All' ||
      scope === 'User.ReadWrite' ||
      scope.startsWith('https://graph.microsoft.com/'),
  );

  // Default to User.Read if no Graph scopes found
  return graphScopes.length > 0 ? graphScopes : ['User.Read'];
}

export const entraScopes = (): string[] => {
  const baseScopes = ['openid', 'profile', 'email'];
  const customApiScope = getCustomApiScope();
  const graphScopes = getMicrosoftGraphScopes();

  const allScopes = [...baseScopes];
  if (customApiScope) {
    allScopes.push(customApiScope);
  }
  allScopes.push(...graphScopes);
  return allScopes;
};

/**
 * Default login request configuration (lazy evaluation)
 * Includes both custom API and Microsoft Graph scopes for initial consent
 */
export const loginRequest = {
  get scopes() {
    return entraScopes();
  },
  prompt: 'select_account' as const,
};

/**
 * Default silent request configuration for token acquisition (lazy evaluation)
 */
export const silentRequest = {
  get scopes() {
    return getScopes();
  },
  forceRefresh: false,
};
